import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Stack, Card, CardContent,
  Chip, Avatar, CircularProgress
} from '@mui/material';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useTenant } from '../contexts/TenantContext';
import { Lead } from '../types';
import LeadDetailModal from '../components/leads/LeadDetailModal';

const STAGES = ["New", "Contacted", "Qualified", "Proposal", "Won", "Lost"];

const Pipeline: React.FC = () => {
  const { organizationId, workspaceId } = useTenant();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (!organizationId || !workspaceId) return;

    const leadsRef = collection(db, 'organizations', organizationId, 'workspaces', workspaceId, 'leads');
    const q = query(leadsRef);

    const unsubscribe = onSnapshot(q, (snapshot: any) => {
      const leadsData: Lead[] = [];
      snapshot.forEach((doc: any) => {
        leadsData.push({ id: doc.id, ...doc.data() } as Lead);
      });
      setLeads(leadsData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [organizationId, workspaceId]);

  const getLeadsByStage = (stage: string) => leads.filter(l => l.stage === stage);

  const handleLeadClick = (lead: Lead) => {
    setSelectedLead(lead);
    setIsModalOpen(true);
  };

  if (loading) return <CircularProgress />;

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Sales Pipeline</Typography>

      <Box sx={{ flexGrow: 1, overflowX: 'auto', pb: 2 }}>
        <Stack direction="row" spacing={2} sx={{ height: '100%', minWidth: 1200 }}>
          {STAGES.map((stage) => (
            <Paper
              key={stage}
              sx={{
                width: 250,
                bgcolor: 'background.default',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={{ p: 2, borderBottom: '2px solid', borderColor: 'primary.main', bgcolor: 'rgba(255,255,255,0.02)' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="bold">{stage}</Typography>
                  <Chip label={getLeadsByStage(stage).length} size="small" variant="outlined" />
                </Stack>
              </Box>

              <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                <Stack spacing={1}>
                  {getLeadsByStage(stage).map((lead) => (
                    <Card
                      key={lead.id}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
                        transition: 'all 0.2s'
                      }}
                      onClick={() => handleLeadClick(lead)}
                    >
                      <CardContent sx={{ p: '12px !important' }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>{lead.name}</Typography>
                        <Typography variant="caption" color="textSecondary" display="block">{lead.source}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                          <Chip
                            label={lead.priority}
                            size="small"
                            sx={{ fontSize: '10px', height: 16 }}
                            color={lead.priority === 'High' ? 'error' : 'default'}
                          />
                          <Box sx={{ flexGrow: 1 }} />
                          <Avatar sx={{ width: 20, height: 20, fontSize: '10px' }}>
                            {lead.assignedTo?.charAt(0) || '?'}
                          </Avatar>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>
            </Paper>
          ))}
        </Stack>
      </Box>

      <LeadDetailModal
        lead={selectedLead}
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </Box>
  );
};

export default Pipeline;
